import { useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { QrCode, Sun, Moon, Send, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import SEO from "@/components/SEO";

export default function ContactPage() {
  const { theme, toggleTheme } = useTheme();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSending(true);
    try {
      await api.post("/marketing/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send message.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="contact-page">
      <SEO
        title="Contact"
        description="Contact Smart-QR for product questions, billing help, or partnership inquiries. We reply to every message as soon as we can."
        canonicalUrl="https://smart-qr.app/contact"
        ogUrl="https://smart-qr.app/contact"
        ogImage="https://smart-qr.app/icon-192.png"
      />
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/40">
        <div className="flex items-center justify-between px-6 py-4 max-w-4xl mx-auto">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center">
              <QrCode className="text-primary-foreground" size={16} />
            </div>
            <span className="font-heading text-base font-bold">Smart-QR</span>
          </Link>
          <button onClick={toggleTheme} className="p-2.5 rounded-xl hover:bg-secondary text-muted-foreground">
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
      </nav>

      <div className="max-w-lg mx-auto px-6 py-16 animate-fade-in">
        {sent ? (
          <div className="text-center py-12" data-testid="contact-success">
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={32} className="text-emerald-500" />
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">Message Sent</h1>
            <p className="text-muted-foreground text-sm mb-8">
              Thanks for reaching out. We'll get back to you as soon as possible.
            </p>
            <Link to="/">
              <Button variant="outline" className="gap-2" data-testid="contact-back-home-btn">
                <ArrowLeft size={16} /> Back to Home
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-3xl font-bold mb-2">Contact Us</h1>
            <p className="text-muted-foreground text-sm mb-8">Have a question or feedback? We'd love to hear from you.</p>

            <Card>
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div>
                    <Label htmlFor="contact-name" className="text-xs">Name</Label>
                    <Input id="contact-name" placeholder="Your name" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="mt-1" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="contact-email" className="text-xs">Email</Label>
                    <Input id="contact-email" type="email" placeholder="you@example.com" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="mt-1" data-testid="contact-email-input" />
                  </div>
                  <div>
                    <Label htmlFor="contact-message" className="text-xs">Message</Label>
                    <textarea id="contact-message" placeholder="How can we help?" value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      rows={5}
                      className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                      data-testid="contact-message-input" />
                  </div>
                  <Button type="submit" className="w-full btn-glow gap-2" disabled={sending} data-testid="contact-submit-btn">
                    {sending ? "Sending..." : <><Send size={16} /> Send Message</>}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
